<template>
  <div class="customer-seating-chart">
    <SDialog v-model:open="isOpen">
      <SDialogContent
        class="customer-seating-chart !fixed !top-0 !right-[3%] !left-auto !translate-x-0 !translate-y-[6vh] !w-[66%] !max-w-[66%] !h-[88vh] !max-h-[88vh] !rounded-md"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("common.seating-plan") }}
          </SDialogTitle>
        </SDialogHeader>
        <div
          class="chart-container"
          style="width: 100%; min-width: 400px; min-height: 600px"
        >
          <div :id="divId" style="min-height: 80vh"></div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { LogLevel, LogService } from "@/services/logging";
import POSConfigModule from "@/store/modules/pos-config";
import { loadSeatsioChartJs } from "@/utils/vue-helpers/seats-io";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "CustomerSeatingChart",
  components: {
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  props: {
    chartData: {
      required: true,
      type: Object as PropType<any>,
    },
  },
  setup(props) {
    const { t } = useI18n({});
    let seatsio = ref<any>(null);
    let chart = ref<any>(null);
    let divId = ref<string>(props.chartData.divId);
    const isOpen = ref<boolean>(false);

    return {
      t,
      seatsio,
      divId,
      chart,
      isOpen,
    };
  },
  async mounted() {
    this.isOpen = true;
    await this.$nextTick();

    try {
      try {
        await loadSeatsioChartJs();
      } catch (error) {
        LogService.info(
          `Seatsio: failed to load the chart.js script on the customer display`,
          { error: String(error), groupId: POSConfigModule.groupId },
          LogLevel.Error
        );
        this.isOpen = false;
        return;
      }
      this.seatsio = (window as any).seatsio;
      this.isChartRendering = true;
      this.renderChart();
      this.isChartRendering = false;
    } finally {
      // this.loading = false;
    }
  },
  methods: {
    async renderChart() {
      try {
        let chartConfig = this.$props.chartData;
        // SeatsIO Docs: In this mode interaction is disabled and session cannot be started nor continued.
        // SeatsIO Docs:
        // In order to mark which seats should be displayed as selected, you may:
        // 1- Set the selectedObjects configuration parameter.
        // 2- Use the chart.doSelectObjects() and chart.deselectObjects() methods.
        // chartConfig.selectedObjects = ["G-B-26"];
        // delete chartConfig.selectedObjectsInputName;
        LogService.info("Customer display chartConfig attached", {
          params: chartConfig,
        });
        this.chart = new this.seatsio.SeatingChart(chartConfig).render();
      } catch (err) {
        LogService.info(
          `Seatsio: failed to create or render the chart on the customer display`,
          { error: String(err), groupId: POSConfigModule.groupId },
          LogLevel.Error
        );
      }
    },
    async selectObjects(newlySelectedObjects: string[]) {
      this.chart.doSelectObjects(newlySelectedObjects);
    },
    async deselectObjects(deselectedObjects: string[]) {
      this.chart.deselectObjects(deselectedObjects);
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
</style>
